import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Text } from '@shopify/polaris';
import { ClockIcon, StatusActiveIcon } from '@shopify/polaris-icons';
import { CardHeader, SURFACE } from '../ui/CardSurface';

/**
 * The two panels that make Home a dashboard rather than one table.
 *
 * Both draw from fields /dashboard-stats gained in the same change
 * (week_activity, on_shift) and both return null when those fields are absent,
 * so this frontend runs unchanged against a backend that has not been deployed
 * yet - the panels simply do not appear.
 *
 * Neither invents anything. The chart is a count of clock-ins per day and the
 * list is the same population the Active Staff card counts, with names against
 * it.
 */

/**
 * 'YYYY-MM-DD' as a date in the VIEWER's timezone.
 *
 * NOT new Date('2026-09-18'), which the spec says to read as UTC midnight -
 * west of Greenwich that renders as the previous day, so every bar would carry
 * the wrong weekday label.
 */
function localDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** 'YYYY-MM-DD HH:MM:SS' (or the T form) as a wall-clock time. */
function clockTime(value, lang) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(String(value || ''));
  if (!m) return '';

  const d = new Date(
    Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]),
  );

  return d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
}

function initialsOf(first, last) {
  return `${String(first || '').charAt(0)}${String(last || '').charAt(0)}`.toUpperCase();
}

/**
 * Clock-ins per day for the last seven days.
 *
 * The track takes the card's height rather than a fixed one, because the row
 * stretches this card to match the list beside it and a fixed track would leave
 * the difference as white space inside the chart. Days with nothing in them
 * still draw - as a 2px rule, not a gap - because a chart that omits its quiet
 * days draws a week that did not happen.
 */
export function WeekActivityChart({ data }) {
  const { t, i18n } = useTranslation();

  // Absent and empty are different claims, and OnShiftPanel already draws that
  // line. Absent means this backend cannot answer - every build older than the
  // deploy that added week_activity - so the card must not appear at all. Empty
  // means the query ran and returned nothing, which is exactly what the
  // controller's catch produces; a card that says so can be reported, whereas a
  // card that silently vanishes reads as a layout bug and leaves no trace.
  if (!Array.isArray(data)) return null;

  const days = data;
  const counts = days.map((d) => Number(d.staff_count) || 0);

  // Headroom, so the week's own peak is never the ceiling. Under max(1, peak) a
  // single clock-in drew a full-height bar - one person rendered pixel-identical
  // to the whole roster. The 1.25 holds the tallest bar near 80% at any scale:
  // peak 1 -> 25%, peak 4 -> 80%, peak 19 -> 79%.
  const peak = Math.max(0, ...counts);
  // Rounded up to a multiple of 4 so the four gridline intervals are whole
  // numbers - a y-axis reading 0 / 0.75 / 1.5 / 2.25 / 3 counts nothing.
  // peak 1 -> 4 (bar at 25%), peak 9 -> 12 (75%), peak 19 -> 24 (79%).
  const max = Math.max(4, Math.ceil((peak * 1.25) / 4) * 4);
  const ticks = Array.from({ length: 5 }, (_, i) => (max / 4) * i);

  const todayKey = (() => {
    const now = new Date();
    const p = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  })();

  // One accessible name for the whole plot rather than a tab stop per column:
  // every value is already printed on screen, so a screen reader needs the
  // series once, on demand.
  const summary = days
    .map((day, i) => {
      const date = localDay(day.date);
      const label = date ? date.toLocaleDateString(i18n.language, { weekday: 'long' }) : '';
      return `${label} ${counts[i]}`;
    })
    .join(', ');

  return (
    <div style={{ ...SURFACE, display: 'flex', flexDirection: 'column' }}>
      <CardHeader icon={ClockIcon} tone="info" title={t('last_7_days')} pad="14px 16px" />

      {/* The body band. The grid row stretches this card to the list beside it
          and the surplus has to land somewhere: inside a bordered, tinted panel
          it becomes a roomier plot, where under the old maxHeight + auto margin
          it became ~180px of unpainted white between the labels and the caption.
          Same mechanism the pricing page uses on its stretched quote box. */}
      <div style={{ padding: '16px', display: 'flex', flex: 1, minHeight: 0 }}>
        {days.length ? (
          <div
            role="img"
            aria-label={`${t('last_7_days')}: ${summary}`}
            style={{
              flex: 1,
              minWidth: 0,
              // A definite height, not one inherited from the row. The bars are
              // percentages of this box, so it has to resolve against a settled
              // number; 200px also fixes the plot at a constant size whatever
              // the list beside it is doing.
              minHeight: '200px',
              background: '#f7f7f7',
              border: '1px solid #ebebeb',
              borderRadius: '10px',
              padding: '14px 14px 10px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* The grid is what makes a quiet week read as a chart rather than
                an empty box. The card is stretched to the list beside it, so the
                plot is tall whatever the data does; ruled and labelled, that
                height is a scale a merchant can read a bar against, where
                unmarked it was just space. */}
            <div style={{ flex: 1, display: 'flex', minHeight: 0, gap: '8px' }}>
              <div style={{ position: 'relative', width: '20px', flex: 'none' }}>
                {ticks.map((tv, ti) => (
                  <span
                    key={tv}
                    style={{
                      position: 'absolute',
                      right: 0,
                      bottom: `${(ti / 4) * 100}%`,
                      transform: 'translateY(50%)',
                      fontSize: '10px',
                      fontWeight: 500,
                      color: '#616161',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                    }}
                  >
                    {tv}
                  </span>
                ))}
              </div>

              <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                {ticks.map((tv, ti) => (
                  <div
                    key={tv}
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      bottom: `${(ti / 4) * 100}%`,
                      height: '1px',
                      // The zero line reads a step stronger than the rest, so
                      // the bars stand on something rather than float between.
                      background: ti === 0 ? '#d4d4d4' : '#ebebeb',
                    }}
                  />
                ))}

                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end' }}>
                  {days.map((day, i) => {
                    const n = counts[i];
                    const date = localDay(day.date);
                    const pct = n ? (n / max) * 100 : 0;

                    return (
                      <div
                        key={day.date || i}
                        title={date ? `${date.toLocaleDateString(i18n.language)} \u2014 ${n}` : String(n)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                        }}
                      >
                        {/* The slot is held whether or not there is a number in
                            it, so a day gaining its first clock-in does not
                            shunt every other bar down by 18px. */}
                        <span
                          style={{
                            flex: 'none',
                            height: '18px',
                            lineHeight: '18px',
                            fontSize: '12px',
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            color: '#303030',
                          }}
                        >
                          {n || ''}
                        </span>

                        <div
                          style={{
                            flex: 'none',
                            // A 28px mark, not a block: at this card width the
                            // old 62% was pinned to its cap and read as a table.
                            width: '46%',
                            maxWidth: '28px',
                            minWidth: '10px',
                            // A quiet day is an off mark on the zero line, not a
                            // grey digit. Five grey zeros used to outweigh the
                            // two real bars.
                            height: n ? `${pct}%` : '2px',
                            minHeight: n ? '3px' : '2px',
                            borderRadius: n ? '4px 4px 0 0' : '1px',
                            background: n ? '#008060' : '#d4d4d4',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Offset by the y-axis (20px) plus its gap (8px) so each label sits
                under its own column. */}
            <div style={{ display: 'flex', marginTop: '8px', paddingLeft: '28px' }}>
              {days.map((day, i) => {
                const date = localDay(day.date);
                const isToday = String(day.date) === todayKey;

                return (
                  <div
                    key={day.date || i}
                    style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}
                  >
                    {/* Every label carries the same padding and radius, so
                        marking today costs no layout - only the colour moves. */}
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: isToday ? 700 : 500,
                        background: isToday ? '#cdfee1' : 'transparent',
                        color: isToday ? '#0c5132' : '#616161',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {date ? date.toLocaleDateString(i18n.language, { weekday: 'short' }) : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              minHeight: '200px',
              background: '#f7f7f7',
              border: '1px solid #ebebeb',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 18px',
              textAlign: 'center',
            }}
          >
            <Text as="p" variant="bodySm" tone="subdued">
              {t('dashboard_empty_no_activity_body')}
            </Text>
          </div>
        )}
      </div>

      {/* The peak, not the sum: staff_count is distinct staff PER DAY, so adding
          the week up double-counts anyone who worked more than one day. The peak
          is also the number the bar heights are drawn against. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '12px 16px',
          borderTop: '1px solid #ebebeb',
          fontSize: '12.5px',
          fontWeight: 500,
          color: '#616161',
        }}
      >
        <span>{t('week_chart_caption')}</span>
        <span
          style={{
            flex: 'none',
            background: '#cdfee1',
            color: '#0c5132',
            borderRadius: '6px',
            padding: '2px 7px',
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {t('week_chart_peak', { count: peak })}
        </span>
      </div>
    </div>
  );
}

/**
 * Who is on shift, and since when.
 *
 * `count` is the authoritative figure - the same active_staff the stat card
 * shows. `people` is capped at five by the API, so the remainder is stated
 * rather than quietly dropped.
 */
export function OnShiftPanel({ people, count }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // An absent field and an empty one are different claims. Absent means this
  // backend cannot answer - which is every backend older than this deploy - and
  // the panel must not appear at all; rendering "nobody is clocked in" under a
  // header saying 9 are is worse than rendering nothing.
  if (!Array.isArray(people)) return null;

  const list = people;
  const total = Number(count) || 0;

  return (
    <div style={{ ...SURFACE, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        icon={StatusActiveIcon}
        tone="success"
        title={t('clocked_in')}
        context={String(total)}
        pad="14px 16px"
      />

      {list.length ? (
        <div role="list" style={{ flex: 1 }}>
          {list.map((person, i) => {
            const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
            const sub = [person.position, person.location_name].filter(Boolean).join(' · ');
            const since = clockTime(person.since, i18n.language);

            return (
              <div
                key={`${name}-${i}`}
                role="listitem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  // 16px matches the header band, so the avatars line up
                  // with the header's disc instead of sitting 2px inside it.
                  padding: '12px 16px',
                  borderTop: i ? '1px solid #ebebeb' : 'none',
                }}
              >
                <span
                  // Decorative: the name follows in the next element, and
                  // without this every row is announced "M H Malik Hammad".
                  aria-hidden="true"
                  style={{
                    width: '28px',
                    height: '28px',
                    flex: 'none',
                    borderRadius: '999px',
                    // Same success pair as the header disc, so green means
                    // one thing on this card: someone is on shift.
                    background: '#cdfee1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#0c5132',
                  }}
                >
                  {initialsOf(person.first_name, person.last_name)}
                </span>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#303030',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {name}
                  </div>
                  {sub && (
                    <div
                      // Recoverable when a long German location truncates.
                      title={sub}
                      style={{
                        fontSize: '12px',
                        color: '#616161',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {sub}
                    </div>
                  )}
                </div>

                {since && (
                  // One interpolated string rather than label-then-value.
                  // Japanese puts its particle after the time, and the bare
                  // `since` key there is the noun "start date", so the old
                  // concatenation rendered "開始日 01:37".
                  <span
                    style={{
                      flex: 'none',
                      fontSize: '12px',
                      color: '#616161',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('since_time', { time: since })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px 18px',
            textAlign: 'center',
          }}
        >
          <Text as="p" variant="bodySm" tone="subdued">
            {t('dashboard_empty_no_activity_body')}
          </Text>
        </div>
      )}

      {/* A sibling of the list, not a child of it: role="list" may contain
          only listitems, and only out here does the flex:1 list push this to
          the card's bottom edge - which is what gives a short list a defined
          bottom band instead of a floating one under white space. */}
      {list.length > 0 && (
        <button
          type="button"
          onClick={() => navigate('/StaffManagement')}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            // 54px = 16 padding + 28 avatar + 10 gap, so the remainder starts
            // under the names rather than under their initials.
            padding: '12px 16px 12px 54px',
            border: 'none',
            borderTop: '1px solid #e3e3e3',
            background: '#f7f7f7',
            fontFamily: 'inherit',
          }}
        >
          <span style={{ fontSize: '12px', color: '#616161' }}>
            {total > list.length ? t('on_shift_more', { count: total - list.length }) : ''}
          </span>
          <span style={{ flex: 'none', fontSize: '12px', fontWeight: 600, color: '#005bd3' }}>
            {t('view_all_staff')}
          </span>
        </button>
      )}
    </div>
  );
}
